import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import React from 'react';

import ShadowerLink from '../../../components/ShadowerLink';

import {
  Col,
  Grid,
  Row,
} from '../../../incubation/components/ResponsiveGridLayout';

import HowItWorksStepBox from './HowItWorksStepBox';

import s from './AboutPageMainContent.scss';

const SHADOWING_EXPERTS_TEXT = [
  `
  What sets amateurs apart from experts is real-world knowledge and hands-on experience.
  But acquiring that knowledge and experience can be difficult without multiple college internships, expensive formal education and connections in the
  industry.`,
  'And this is where Shadower comes in.',
  `
  With Shadower, you can finally learn the tools and techniques of experts and professionals across a range of fields through private or public
  shadowing sessions that vary from observation-only to fully interactive, depending on the activity.
  You will be able to take your skillset to the next level through one-on-one sessions and receive immediate feedback on your own work.
  `,
  'Sign up today to discover your next great passion in life.',
];

const LET_PEOPLE_SHADOW_YOU_TEXT = [
  `
  Our experts are everyday artists, creators, producers, entrepreneurs and professionals looking to share their knowledge with others in a meaningful
  and impactful way by building and maintaining relationships with others in their local communities.
  In order to let people shadow you, we only require that you are knowledgeable in some area and eager to share that knowledge.`,
  `
  Whether you have a job that students might consider pursuing as a future career, deep knowledge in a specific field, mastery of a type of tool or
  own a piece of technology that others might enjoy learning how to use, Shadower is the perfect channel for you to share your passion with the world
  and supplement your income along the way.
  During our beta phase, new categories will be added regularly.`,
  'Sign up today and start letting people shadow you.',
];

const WHY_USE_SHADOWER_BULLET_ITEMS_FOR_SHADOWERS = [
  'Learn the tips and tricks of the pros',
  'Establish professional connections',
  'Learn new skills',
  'Find the right career path',
  'Hands-on training with professional-level tech',
];

const WHY_USE_SHADOWER_BULLET_ITEMS_FOR_EXPERTS = [
  'Earn extra income by sharing your knowledge',
  'Share your passion for education',
  'Connect more dynamically with your community',
  'Expand your brand organically',
  'Cultivate new part-time or full-time employees',
];

const HOW_IT_WORKS_STEP_PROPS = [
  {
    key: 'step-1',
    title: 'Sign up',
    instructions: ['Create a profile and add your information'],
  },
  {
    key: 'step-2',
    title: 'Find an expert',
    instructions: ['Search for a job, skill or technology that interests you'],
  },
  {
    key: 'step-3',
    title: 'Pick a session',
    instructions: [
      'Choose a specific shadowing session that you find interesting',
    ],
  },
  {
    key: 'step-4',
    title: 'Connect with your expert',
    instructions: [
      'Shadow your expert in real life',
      'Gain hands-on knowledge',
      'Learn the tips and tricks of the professionals',
      'Discover new hobbies and possible careers',
    ],
  },
];

class AboutPageMainContent extends React.Component {
  static propTypes = {};

  render() {
    return (
      <React.Fragment>
        <section className={s.aboutShadowerSection}>
          <Grid>
            <Row>
              <Col>
                <div className={s.introSection}>
                  <div className={s.introSectionText}>
                    <h2
                      className={cx('globalSectionHeading', 'text-align-right')}
                    >
                      What we are all about
                    </h2>
                    <p>
                      Shadower is an online platform that helps amateurs find
                      experts to shadow in real life. Our mission is to
                      reimagine how education works in an age when experience
                      and connections matter most.
                    </p>
                    <ShadowerLink
                      className={s.faqLink}
                      look="outline"
                      to="/help"
                    >
                      View FAQs
                    </ShadowerLink>
                  </div>
                  <div>
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video
                      className={s.introSectionVideo}
                      controls
                      poster="/assets/images/about/poster.svg"
                    >
                      <source src="/assets/video/Shadower.mp4" />
                      <p>Your browser does not support this video.</p>
                    </video>
                  </div>
                </div>
              </Col>
            </Row>
          </Grid>
        </section>
        <section className={s.howItWorksSection}>
          <Grid>
            <Row>
              <Col>
                <h2 className="globalSectionHeading text-align-center">
                  How it works
                </h2>
                <ol className={s.howItWorksSteps}>
                  {HOW_IT_WORKS_STEP_PROPS.map((props, idx) => (
                    <HowItWorksStepBox {...props} stepNum={idx + 1} />
                  ))}
                </ol>
              </Col>
            </Row>
          </Grid>
        </section>
        <section className={s.shadowingExpertsSection}>
          <Grid>
            <Row>
              <Col>
                <div className={s.splitLayout}>
                  <div className={s.splitLayoutText}>
                    <h2 className="globalSectionHeading text-align-center">
                      Shadowing experts
                    </h2>
                    {SHADOWING_EXPERTS_TEXT.map((text, idx) => {
                      const key = `text-${idx}`;
                      return <p key={key}>{text}</p>;
                    })}
                    <ShadowerLink
                      className={s.links}
                      to="/shadowings/areas-of-expertise"
                    >
                      Discover experts to shadow
                    </ShadowerLink>
                  </div>
                  <div className={s.splitLayoutImage}>
                    <img
                      alt="Music producer"
                      src="/assets/images/about/music-producer@1280x800.jpg"
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Grid>
        </section>
        <section className={s.letPeopleShadowYouSection}>
          <Grid>
            <Row>
              <Col>
                <div className={s.splitLayout}>
                  <div className={s.splitLayoutImage}>
                    <img
                      alt="Craftsman"
                      src="/assets/images/about/craftsman@1280x800.jpg"
                    />
                  </div>
                  <div className={s.splitLayoutText}>
                    <h2 className="globalSectionHeading text-align-center">
                      Let people shadow you
                    </h2>
                    {LET_PEOPLE_SHADOW_YOU_TEXT.map((text, idx) => {
                      const key = `text-${idx}`;
                      return <p key={key}>{text}</p>;
                    })}
                    <ShadowerLink className={s.links} to="/handbook">
                      Get started earning revenue
                    </ShadowerLink>
                  </div>
                </div>
              </Col>
            </Row>
          </Grid>
        </section>

        <section className={s.location}>
          <h2 className="globalSectionHeading text-align-center">
            Where We're Located
          </h2>
          <p>
            We are currently beta-testing in Colorado. If you would like to help
            us bring Shadower to your state, contact us!
          </p>
          <img
            alt="Map of the US"
            className={s.map}
            src="/assets/images/about/team/us-vector-map.png"
          />
        </section>

        <section className={s.whyUseShadowerSection}>
          <Grid>
            <Row>
              <Col>
                <h2
                  className={cx(
                    'globalSectionHeading',
                    'text-align-center',
                    'globalWhite',
                  )}
                >
                  Why use Shadower?
                </h2>
                <div className={s.whyUseShadowerContainer}>
                  <div>
                    <h6>Shadowers</h6>
                    <ul>
                      {WHY_USE_SHADOWER_BULLET_ITEMS_FOR_SHADOWERS.map(
                        (text, idx) => {
                          const key = `text-${idx}`;
                          return <li key={key}>{text}</li>;
                        },
                      )}
                    </ul>
                  </div>
                  <div>
                    <h6>Experts</h6>
                    <ul>
                      {WHY_USE_SHADOWER_BULLET_ITEMS_FOR_EXPERTS.map(
                        (text, idx) => {
                          const key = `text-${idx}`;
                          return <li key={key}>{text}</li>;
                        },
                      )}
                    </ul>
                  </div>
                </div>
                <div className={s.whyUseShadowerLinks}>
                  <ShadowerLink to="/register">Sign Up</ShadowerLink>
                  <ShadowerLink look="inverse" to="/help">
                    View FAQs
                  </ShadowerLink>
                </div>
              </Col>
            </Row>
          </Grid>
        </section>
      </React.Fragment>
    );
  }
}

export default withStyles(s)(AboutPageMainContent);
